import { I18n } from "@assets/strings";
import React from "react";
import styled, { useTheme } from "styled-components";
import phoneIcon from "@assets/images/auth/Phone.svg";
import appleIcon from "@assets/images/auth/apple.svg";
import facebookIcon from "@assets/images/auth/facebook.svg";
import googleIcon from "@assets/images/auth/google.svg";
import { config } from "@core/config/config";
import { useServices } from "@core/servicesContext";
import { SocialAuthProvider } from "@domain/auth/authProvider";
import { useAsync } from "@utils/hooks";
import { PhoneNumberAuthModal } from "@ui/components/auth/phoneNumberAuthModal";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { PrimaryMedium } from "@ui/components/containers/primary";
import { useModalTools } from "@ui/modals/modalContext";
import { gap } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";
import { Tooltipable } from "@ui/tooltips/tooltipable";

interface SocialLoginProps extends React.HTMLAttributes<HTMLDivElement> {
	message?: string;
}

export const SocialLogin: React.FC<SocialLoginProps> = ({ message, ...htmlProps }) => {
	const { currentProfileService, trackingService } = useServices();

	const { run, loading } = useAsync(async (authProvider: SocialAuthProvider) => {
		try {
			await currentProfileService.login(authProvider);
			trackingService.track("login", { provider: authProvider });
		} catch (e) {
			console.error(e);
			createToast(I18n.t("error"), I18n.t("auth.error.default"), "error");
		}
	}, []);

	const { createModal } = useModalTools();

	const openPhoneNumberAuthModal = (): void => {
		createModal((close) => <PhoneNumberAuthModal close={close} />);
	};

	const theme = useTheme();

	return (
		<Container {...htmlProps}>
			{message && <PrimaryMedium>{message}</PrimaryMedium>}
			<Buttons>
				{config.features.login.phone ? (
					<Button loading={loading} loadingColor={theme.colors.primary} onClick={() => openPhoneNumberAuthModal()}>
						<img src={phoneIcon} />
					</Button>
				) : (
					<Tooltipable text="Bientôt disponible" dark>
						<div style={{ opacity: 0.5 }}>
							<Button loading={loading} loadingColor={theme.colors.primary}>
								<img src={phoneIcon} />
							</Button>
						</div>
					</Tooltipable>
				)}
				{config.features.login.facebook && (
					<Button loading={loading} loadingColor={theme.colors.primary} onClick={() => run("Facebook")}>
						<img src={facebookIcon} />
					</Button>
				)}
				<Button loading={loading} loadingColor={theme.colors.primary} onClick={() => run("Google")}>
					<img src={googleIcon} />
				</Button>
				{config.features.login.apple && (
					<Button loading={loading} loadingColor={theme.colors.primary} onClick={() => run("Apple")}>
						<img src={appleIcon} />
					</Button>
				)}
			</Buttons>
		</Container>
	);
};

const Container = styled.div`
	display: flex;
	flex-direction: column;
	${gap(20)}
`;

const Buttons = styled.div`
	display: flex;
	${gap(20, "row")};
`;

const Button = styled(LoadingButton)`
	background-color: ${({ theme }) => theme.colors.white};
	box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.1);
	&:hover {
		background-color: ${({ theme }) => theme.colors.lightergray};
	}
	color: ${({ theme }) => theme.colors.primary};
	padding: 0;
	width: 40px;
	height: 40px;
	border-radius: 20px;
	text-align: center;

	img {
		max-width: 16px;
	}
`;
